import { useRef, useState } from "react";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { useLang, tx } from "@/context/LangContext";
import { T } from "@/data/content";
import { FadeUp, SectionLabel, Portal } from "@/components/Reveal";
import { Sparkles, X } from "lucide-react";

const GALLERY = [
    { src: "/img/laduree-affiche.jpg", cap: { en: "OFFICIAL POSTER", fr: "AFFICHE OFFICIELLE" }, cls: "md:row-span-2", ar: "aspect-[3/4]" },
    { src: "/img/laduree-02.jpg", cap: { en: "THE POUR", fr: "LE COULIS" }, cls: "", ar: "aspect-[4/5]" },
    { src: "/img/laduree-03.jpg", cap: { en: "LEVITATION", fr: "LÉVITATION" }, cls: "", ar: "aspect-[4/5]" },
    { src: "/img/laduree-04.jpg", cap: { en: "THE DRIP", fr: "LA GOUTTE" }, cls: "", ar: "aspect-[3/2]" },
    { src: "/img/laduree-05.jpg", cap: { en: "SUSPENDED", fr: "EN SUSPENS" }, cls: "", ar: "aspect-[3/2]" },
];

export default function Laduree() {
    const { lang } = useLang();
    const [zoom, setZoom] = useState(null);
    const [open, setOpen] = useState(false);
    const secRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: secRef,
        offset: ["start end", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

    return (
        <section
            id="laduree"
            data-testid="laduree-section"
            ref={secRef}
            className="relative overflow-hidden bg-ink py-24 text-cream md:py-36"
        >
            {/* green & pink wash derived from the poster — 40% light */}
            <div
                className="pointer-events-none absolute inset-0"
                style={{
                    background:
                        "radial-gradient(70% 60% at 70% 40%, rgba(24,54,30,0.40) 0%, transparent 70%), radial-gradient(55% 55% at 20% 80%, rgba(214,110,160,0.30) 0%, transparent 70%)",
                }}
            />
            <div className="relative mx-auto max-w-[1600px] px-5 md:px-10">
                <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
                    <div>
                        <SectionLabel
                            en={tx(lang, T.laduree.label)}
                            fr={tx(lang, T.laduree.label)}
                        />
                        <FadeUp delay={0.1}>
                            <h2 className="mt-8 font-display text-[clamp(2.6rem,8vw,7rem)] font-extrabold uppercase leading-none tracking-[-0.02em]">
                                LADURÉE<span className="text-violet">.</span>
                            </h2>
                        </FadeUp>
                    </div>
                    <FadeUp delay={0.2}>
                        <p className="flex max-w-xs items-start gap-3 font-mono text-[9px] leading-relaxed tracking-[0.3em] text-cream/40 md:text-[10px]">
                            <Sparkles size={14} className="mt-0.5 shrink-0 text-gold" />
                            {tx(lang, T.laduree.note)}
                        </p>
                    </FadeUp>
                </div>

                {/* poster forward — l'affiche en avant */}
                <FadeUp delay={0.1} className="mt-12 md:mt-16">
                    <div className="flex flex-col items-center gap-8 md:flex-row md:items-end md:gap-14">
                        <button
                            data-testid="laduree-poster"
                            data-cursor="VIEW"
                            onClick={() => setZoom({ src: "/img/laduree-affiche.jpg", cap: tx(lang, T.laduree.poster) })}
                            className="group relative block w-full max-w-md overflow-hidden text-left md:w-[38%]"
                        >
                            <div className="relative aspect-[3/4] w-full overflow-hidden">
                                <motion.img
                                    src="/img/laduree-affiche.jpg"
                                    alt="LADURÉE — affiche officielle, campagne visuelle par Gloom Ent."
                                    style={{ y }}
                                    className="absolute inset-0 h-[116%] w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent" />
                            </div>
                        </button>
                        <div className="pb-2 text-center md:text-left">
                            <p className="font-mono text-[9px] tracking-[0.3em] text-gold md:text-[10px]">
                                {tx(lang, T.laduree.client)}
                            </p>
                            <h3 className="mt-2 font-display text-2xl font-extrabold uppercase tracking-tight md:text-4xl">
                                {tx(lang, T.laduree.campaign)}
                            </h3>
                            <p className="mt-2 font-mono text-[9px] tracking-[0.3em] text-cream/60 md:text-[10px]">
                                {tx(lang, T.laduree.poster)} — 2026
                            </p>
                            <button
                                data-testid="laduree-more-btn"
                                data-cursor="VIEW"
                                onClick={() => setOpen((o) => !o)}
                                className="mt-6 inline-flex items-center gap-3 border border-cream/30 px-6 py-3 font-mono text-[10px] tracking-[0.3em] text-cream transition-all duration-500 hover:border-gold hover:text-gold"
                            >
                                {open ? "−" : "+"} {tx(lang, T.laduree.more)}
                            </button>
                        </div>
                    </div>
                </FadeUp>

                {/* gallery — revealed by VOIR + */}
                <AnimatePresence initial={false}>
                    {open && (
                        <motion.div
                            key="laduree-gallery"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                            className="overflow-hidden"
                        >
                            <div className="mt-6 grid grid-cols-1 gap-4 pt-6 sm:grid-cols-2 md:mt-8 md:grid-cols-4 md:gap-6">
                    {GALLERY.map((g, i) => (
                        <motion.button
                            key={g.src}
                            data-testid={`laduree-photo-${i}`}
                            data-cursor="VIEW"
                            onClick={() => setZoom({ src: g.src, cap: tx(lang, g.cap) })}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.15 }}
                            transition={{ duration: 0.8, delay: (i % 4) * 0.07, ease: [0.16, 1, 0.3, 1] }}
                            className={`group relative block w-full overflow-hidden text-left ${g.cls}`}
                        >
                            <div className={`relative w-full overflow-hidden ${g.ar}`}>
                                <img
                                    src={g.src}
                                    alt={`LADURÉE — ${tx(lang, g.cap)}`}
                                    loading="lazy"
                                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1100ms] ease-out group-hover:scale-[1.05]"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                            </div>
                            <p className="mt-2 font-mono text-[8px] tracking-[0.25em] text-cream/50 md:text-[9px]">
                                {tx(lang, g.cap)}
                            </p>
                        </motion.button>
                    ))}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* lightbox */}
            <Portal><AnimatePresence>
                {zoom && (
                    <motion.div
                        data-testid="laduree-lightbox"
                        className="fixed inset-0 z-[80] flex items-center justify-center bg-ink/95 p-5 backdrop-blur-md"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setZoom(null)}
                    >
                        <button
                            data-testid="laduree-lightbox-close"
                            className="absolute right-6 top-6 flex h-11 w-11 items-center justify-center border border-white/20 text-cream transition-colors hover:border-violet hover:text-violet"
                            aria-label="Close"
                        >
                            <X size={16} />
                        </button>
                        <motion.figure
                            initial={{ scale: 0.92, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                            className="max-h-[85vh] max-w-[90vw]"
                        >
                            <img
                                src={zoom.src}
                                alt={zoom.cap}
                                className="max-h-[80vh] max-w-full object-contain"
                            />
                            <figcaption className="mt-3 text-center font-mono text-[9px] tracking-[0.3em] text-cream/50">
                                LADURÉE — {zoom.cap}
                            </figcaption>
                        </motion.figure>
                    </motion.div>
                )}
            </AnimatePresence></Portal>
        </section>
    );
}
